<script setup lang="ts">
import { DispatchStatus, DispatchType } from "@/enums/mission";
import { timeAgo } from "@/modules/common";
import { MateDispatch } from "@/types/entity";
import { useRouter } from "vue-router";

import { SettlementStatus } from "@/enums/mateEnums";
import { useMate } from "@/stores/mate";
import { useGlobalUser } from "@/stores/globalUser";

const router = useRouter();

const mateStore = useMate();
const userStore = useGlobalUser();

const props = defineProps<{
  item: MateDispatch;
  isSelect?: boolean;
  include?: number[];
  type?: "rank";
}>();

const emit = defineEmits<{
  (e: "select", item: MateDispatch): void;
}>();

const getDispatchStatus = (dispatch: MateDispatch): DispatchStatus => {
  if (!mateStore.isMyMateById(dispatch.mateId)) {
    if (
      dispatch.status === DispatchStatus.Settled ||
      dispatch.status === DispatchStatus.Returned
    ) {
      // 随行人员的情况
      const { dispatchMemberzz } = dispatch;
      // 使用 some 替代 forEach，找到符合条件的就返回 true
      const hasPendingSettlement = dispatchMemberzz?.some(
        (item) =>
          item.mate?.userId === userStore.user?.id &&
          item.settlementStatus == SettlementStatus.Pending
      );

      if (hasPendingSettlement) {
        return DispatchStatus.Returned;
      }
      return DispatchStatus.Settled;
    }
  }
  return dispatch.status;
};

const clickMateMission = async (mateDispatch: MateDispatch) => {
  router.push({
    name: "missionDetail",
    query: { dispatchUuid: mateDispatch.uuid },
  });
};

const clickItem = () => {
  if (props.isSelect) {
    emit("select", props.item);
  } else if (props.type === "rank") {
    router.push({
      name: "missionDetail",
      query: { dispatchUuid: props.item.uuid, type: "rank" },
    });
  }
};
</script>

<template>
  <div class="card border-base-200 rounded-lg p-2 mx-2 shadow-sm">
    <div class="flex justify-between items-center text-right">
      <div v-if="isSelect" class="ml-2">
        <VanCheckbox
          :model-value="include?.includes(item.id)"
          @click="emit('select', item)"
        />
      </div>
      <div
        class="flex justify-between items-center text-right flex-1"
        @click="clickItem"
      >
        <MissionParticipantsSelect
          :class="[isSelect ? 'pointer-events-none' : '']"
          :dispatch-mission="item"
          :is-edit="false"
          :is-select="isSelect"
        />

        <div class="flex flex-col items-end gap-1">
          <h3 class="text-sm font-bold">
            {{
              item.type === DispatchType.Stroll ? "溜达" : item.mission?.name
            }}
          </h3>
          <template v-if="type === 'rank'">
            <p>
              难度：
              {{ $t(`mission.difficultyLevel.${item.difficultyLevel}`) }}
            </p>
            <p>活跃分数：{{ item.obtainedScore }}</p>

            <p
              :class="[
                'text-xs',
                item.isSuccess ? 'text-success' : 'text-error',
              ]"
            >
              任务{{ item.isSuccess ? "成功" : "失败" }}
            </p>

            <button class="btn btn-xs" v-if="!isSelect">
              {{ $t(`interactive.reviewPage.goToView`) }}
            </button>
          </template>
          <template v-else>
            <p
              :class="[
                'text-xs',
                getDispatchStatus(item) === DispatchStatus.Returned
                  ? 'text-primary'
                  : 'text-base-content/50',
              ]"
            >
              {{ $t(`mission.dispatchStatusMap.${getDispatchStatus(item)}`) }}
            </p>
            <template v-if="item.type === DispatchType.Mission">
              <p class="text-xs text-base-content/50 line-clamp-1">
                地点：{{ item.location?.name }}
              </p>
            </template>

            <p class="text-xs text-base-content/50">
              创建时间：{{ timeAgo(item.dtCreate) }}
            </p>

            <div class="flex items-center gap-1">
              <button
                v-if="item"
                class="btn btn-xs rounded"
                :class="{
                  'btn-primary': item.status === DispatchStatus.Dispatched,
                }"
                @click.stop="
                  router.push({
                    name: 'missionDialgue',
                    params: { dispatchUuid: item.uuid },
                  })
                "
              >
                {{
                  DispatchStatus.Dispatched === item.status
                    ? "消息"
                    : "历史消息"
                }}
              </button>
              <button
                class="btn btn-xs rounded"
                @click="clickMateMission(item)"
                v-if="!isSelect"
              >
                {{ $t(`interactive.reviewPage.goToView`) }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-1 px-2">
      <div
        v-for="journal in item.obtainedJournalzz"
        :key="journal.id"
        class="bg-base-300 p-2 rounded-lg"
      >
        <VanTextEllipsis
          :content="journal.content"
          :rows="3"
          expand-text="展开"
          collapse-text="收起"
        />
      </div>
    </div>
  </div>
</template>
